import { Injectable } from "@angular/core";
import { RcLoggerService } from "./rcLoggerService";

@Injectable({ providedIn: 'root' })
export class RcStore {
    private localStorageEnabled = true;
    private localStorage: Storage | null = null;

    constructor(private mcrcLog: RcLoggerService) { }

    public setStorageProvider(storageProvider: Storage): void {
        this.localStorage = storageProvider;
    }

    public useLocalStorage(boolForUse: boolean): void {
        this.localStorageEnabled = boolForUse;
    }

    public supportsHtml5Storage(): boolean {
        if (!this.localStorageEnabled || this.localStorage === undefined) return false;
        try {
            const supported = 'localStorage' in window && window['localStorage'] !== null;
            if (supported) this.localStorage = window.localStorage;
            return supported;
        } catch (e) {
            return false; // exception only raised in old firefox due to bug
        }
    }

    public storeImage(key: string, image: HTMLImageElement): void {
        // imageFile: stringToStore;
        if (this.supportsHtml5Storage()) {
            const imgCanvas = document.createElement("canvas");
            const imgContext = imgCanvas.getContext("2d");

            // Set canvas size
            imgCanvas.width = image.width;
            imgCanvas.height = image.height;

            imgContext.drawImage(image, 0, 0);

            const imageFile = {
                image: imgCanvas.toDataURL("image/png"),
                date: new Date()
            };

            try {
                this.localStorage.setItem(key, JSON.stringify(imageFile));
            }
            catch (e) {
                this.mcrcLog.debug("Storage Exception: " + e);
            }
        }
    }

    public loadImage(key: string, expiryMinutes: number = 0): string | null {
        if (this.supportsHtml5Storage()) {
            const storedImage = this.localStorage.getItem(key);
            if (storedImage == null) return null;

            const imageFile = JSON.parse(storedImage);
            if (expiryMinutes > 0 && (!imageFile.date || (Date.now() - imageFile.date) >= expiryMinutes * 60000)) return null;

            return imageFile.image;
        } else {
            return null;
        }
    }

    public getImageLoadDate(key: string): Date | null {
        if (this.supportsHtml5Storage()) {
            const storedImage = this.localStorage.getItem(key);
            if (storedImage == null) return null;

            return JSON.parse(storedImage).date;
        } else {
            return null;
        }
    }

    public storeString(key: string, string: string): void {
        if (this.supportsHtml5Storage()) {
            const stringToStore = {
                string: string,
                date: new Date()
            };

            try {
                this.localStorage.setItem(key, JSON.stringify(stringToStore));
            }
            catch (e) {
                this.mcrcLog.debug("Storage Exception: " + e);
            }
        } else {
            return null;
        }
    }

    public loadString(key: string, expiryMinutes?: number): string | null {
        if (this.supportsHtml5Storage()) {
            const storedString = this.localStorage.getItem(key);
            if (storedString == null) return null;
            const stringToStore = JSON.parse(storedString);
            if (expiryMinutes > 0 && (!stringToStore.date || (Date.now() - stringToStore.date) >= expiryMinutes * 60000)) return null;
            return stringToStore.string;
        } else {
            return null;
        }
    }

    public storeToken(key: string, token: any): void {
        if (this.supportsHtml5Storage()) {
            const tokenToStore = {
                token: token,
                date: new Date()
            };

            try {
                this.localStorage.setItem(key, JSON.stringify(tokenToStore));
            }
            catch (e) {
                this.mcrcLog.debug("Storage Exception: " + e);
            }
        } else {
            return null;
        }
    }

    public loadToken(key: string): any | null {
        if (this.supportsHtml5Storage()) {
            const storedString = this.localStorage.getItem(key);
            if (storedString == null) return null;
            return JSON.parse(storedString).token;
        } else {
            return null;
        }
    }

    public storeBool(key: string, value: boolean): void {
        if (this.supportsHtml5Storage()) {
            const stringToStore = {
                bool: value,
                date: new Date()
            };


            try {
                this.localStorage.setItem(key, JSON.stringify(stringToStore));
            }
            catch (e) {
                this.mcrcLog.debug("Storage Exception: " + e);
            }
        }
    }

    public loadBool(key: string, defaultValue: boolean): boolean {
        if (this.supportsHtml5Storage()) {
            const storedString = this.localStorage.getItem(key);
            if (storedString == null) return defaultValue;
            const value = JSON.parse(storedString).bool;
            return value === undefined ? defaultValue : value;
        }
        return null;
    }
}
